import axios, { AxiosError } from 'axios';
import { AuthService } from '../authService';
import { ApiError } from '@/api/backend/types';
import { getLocalizedValue } from '@/lib/utils';
import i18next from 'i18next';
import { ExternalToast } from 'sonner';

// Extend Axios types to include skipAuth property
declare module 'axios' {
  interface AxiosRequestConfig {
    isPublicEndpoint?: boolean;
    responseInterceptorConfig?: {
      ignoreStatusCodes?: number[];
    };
  }
  interface InternalAxiosRequestConfig {
    isPublicEndpoint?: boolean;
    responseInterceptorConfig?: {
      ignoreStatusCodes?: number[];
    };
  }
}

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
});

const flowableApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}`,
});

let toastFunction:
  ((params: { message: string; variant: 'success' | 'destructive'; options?: ExternalToast }) => void) | null = null;
let navigateFunction: ((options: { to: string; replace?: boolean }) => void) | null = null;

export const setToastFunction = (toast: typeof toastFunction) => {
  toastFunction = toast;
};

export const setNavigateFunction = (navigate: typeof navigateFunction) => {
  navigateFunction = navigate;
};

// Error code to translation key mapping for info alerts
const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  ERR_INVESTOR_COMPANY_SERVICE: 'api.errors.ERR_INVESTOR_COMPANY_SERVICE',
  // Add more error codes here as needed
  // 'ERR_ANOTHER_CODE': 'api.errors.ERR_ANOTHER_CODE',
};

const handleApiError = (error: AxiosError): Promise<never> => {
  // If the error is in the ignore status codes, return the error. Means error is not handled by the interceptor.
  if (
    error.response?.status &&
    error.config?.responseInterceptorConfig?.ignoreStatusCodes?.includes(error.response?.status)
  ) {
    return Promise.reject(error);
  }

  if (
    (error.response?.status === 400 ||
      error.response?.status === 409 ||
      error.response?.status === 403 ||
      error.response?.status === 500 ||
      error.response?.status === 404) &&
    error.response?.data &&
    toastFunction
  ) {
    const apiError = error.response.data as ApiError;
    const gupCanReturn = sessionStorage.getItem('gup.active') === 'true' && !!sessionStorage.getItem('gup.callbackUrl');
    if (gupCanReturn && (apiError.code === 'ERR_ACCESS_DENIED' || apiError.code === 'ERR_INVESTOR_COMPANY_SERVICE')) {
      return Promise.reject(error);
    }

    // Handle specific error codes with info alerts
    if (apiError.code && ERROR_CODE_TRANSLATION_KEYS[apiError.code]) {
      const translationKey = ERROR_CODE_TRANSLATION_KEYS[apiError.code];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const localizedMessage = i18next.t(translationKey as any) as string;

      toastFunction({
        message: localizedMessage,
        variant: 'destructive',
      });
    } else if (apiError.message) {
      let message = '';
      const localizedMessage = getLocalizedValue(apiError.message, i18next.language);
      const applicationNumber = apiError.validationErrors?.applicationNumber;
      if (applicationNumber) {
        message = `${localizedMessage}: ${applicationNumber} ${apiError.correlationId ? `\n${i18next.t('api.errors.referenceId', { id: apiError.correlationId })}` : ''}`;
      } else {
        message = `${localizedMessage} ${apiError.correlationId ? `\n${i18next.t('api.errors.referenceId', { id: apiError.correlationId })}` : ''}`;
      }
      toastFunction({
        message,
        variant: 'destructive',
        options: {
          duration: 30000,
        },
      });
    }
    if (apiError.code === 'ERR_ACCESS_DENIED' || (apiError.code && ERROR_CODE_TRANSLATION_KEYS[apiError.code])) {
      if (navigateFunction) {
        navigateFunction({ to: '/dashboard/workflows', replace: true });
      } else {
        // Fallback to window.location if navigate function is not available
        window.location.href = '/dashboard/workflows';
      }
    }
  }
  return Promise.reject(error);
};

api.interceptors.request.use((config) => {
  // Skip authorization if explicitly disabled
  if (config.isPublicEndpoint) {
    return config;
  }

  const oidcUser = AuthService.getOidcUserFromLocalStorage();

  if (oidcUser) {
    config.headers.Authorization = `Bearer ${oidcUser.access_token}`;
  }

  return config;
});

flowableApi.interceptors.request.use((config) => {
  const requestUrl = config.url ?? '';
  const strapiHost = new URL(import.meta.env.VITE_STRAPI_URL).host;

  if (requestUrl.includes(strapiHost)) {
    return config;
  }

  const oidcUser = AuthService.getOidcUserFromLocalStorage();

  if (oidcUser) {
    config.headers.Authorization = `Bearer ${oidcUser.access_token}`;
  }

  return config;
});

api.interceptors.response.use((response) => response, handleApiError);

export { api, flowableApi };
